import axios from 'axios';

// Base API URL – adjust if you use a different env variable pattern
// const API_URL = 'https://app.polarcut.io/api';
// const API_URL = 'http://localhost:5410/api';
const API_URL = 'https://app.polarcut.io/api';

class CreditService {
  /**
   * Fetch overview of all organizations with credits and users
   */
  static async getOrganizationsOverview({ page = 1, limit = 20, includeUsers = true, searchEmail = '', searchOrgName = '', showPaidOnly = false } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (!includeUsers) params.set('includeUsers', 'false');
    if (searchEmail) params.set('searchEmail', searchEmail);
    if (searchOrgName) params.set('searchOrgName', searchOrgName);
    if (showPaidOnly) params.set('showPaidOnly', 'true');
    const response = await axios.get(`${API_URL}/organizations/overview?${params.toString()}`);
    return response.data; // { organizations, page, limit, total, totalPages }
  }

  /**
   * Add credits to an organization
   * @param {number|string} organizationId
   * @param {number} creditsToAdd
   * @param {string} reason
   */
  static async addCredits(organizationId, creditsToAdd, reason = 'Manual credit addition') {
    const response = await axios.post(`${API_URL}/organizations/${organizationId}/credits/add`, {
      creditsToAdd,
      reason
    });
    return response.data;
  }

  /**
   * Add upload credits to an organization
   * @param {number|string} organizationId
   * @param {number} uploadCreditsToAdd
   * @param {string} reason
   */
  static async addUploadCredits(organizationId, uploadCreditsToAdd, reason = 'Manual upload credit addition') {
    const response = await axios.post(`${API_URL}/organizations/${organizationId}/upload-credits/add`, {
      uploadCreditsToAdd,
      reason
    });
    return response.data;
  }

  /**
   * Create a new organization
   * @param {Object} data { name, domain }
   */
  static async createOrganization(data) {
    const response = await axios.post(`${API_URL}/organizations`, data);
    return response.data;
  }

  /**
   * Add a user to an organization
   * @param {number|string} organizationId
   * @param {string} userEmail
   * @param {string} role
   */
  static async addUserToOrganization(organizationId, userEmail, role = 'member') {
    const response = await axios.post(`${API_URL}/organizations/${organizationId}/users`, {
      email: userEmail,
      role
    });
    return response.data;
  }

  /**
   * Remove credits from an organization
   */
  static async removeCredits(organizationId, creditsToRemove, reason = 'Manual credit deduction') {
    const response = await axios.post(`${API_URL}/organizations/${organizationId}/credits/remove`, {
      creditsToRemove,
      reason
    });
    return response.data;
  }

  /**
   * Remove upload credits from an organization
   * @param {number|string} organizationId
   * @param {number} uploadCreditsToRemove
   * @param {string} reason
   */
  static async removeUploadCredits(organizationId, uploadCreditsToRemove, reason = 'Manual upload credit deduction') {
    const response = await axios.post(`${API_URL}/organizations/${organizationId}/upload-credits/remove`, {
      uploadCreditsToRemove,
      reason
    });
    return response.data;
  }

  /**
   * Remove user from organization
   */
  static async removeUserFromOrganization(organizationId, userUid) {
    const response = await axios.delete(`${API_URL}/organizations/${organizationId}/users/${userUid}`);
    return response.data;
  }

  /**
   * Switch organization's subscription plan
   */
  static async switchPlan(organizationId, data) {
    const response = await axios.put(`${API_URL}/organizations/${organizationId}/subscription`, data);
    return response.data;
  }

  /**
   * Update an organization's trial expiry date
   * @param {number|string} organizationId
   * @param {string} trialEndDate - Date string (YYYY-MM-DD) or ISO timestamp
   */
  static async updateTrialExpiry(organizationId, trialEndDate) {
    const response = await axios.put(`${API_URL}/organizations/${organizationId}/subscription`, {
      trialEndDate
    });
    return response.data;
  }

  /**
   * Update organization name
   * @param {number|string} organizationId
   * @param {string} newName
   */
  static async updateOrganizationName(organizationId, newName) {
    const response = await axios.put(`${API_URL}/organizations/${organizationId}/name`, {
      name: newName
    });
    return response.data;
  }

  /**
   * Delete an organization
   * @param {number|string} organizationId
   */
  static async deleteOrganization(organizationId) {
    const response = await axios.delete(`${API_URL}/organizations/${organizationId}`);
    return response.data;
  }

  /**
   * Move user between organizations
   * @param {string} userEmail
   * @param {number|string} fromOrgId
   * @param {number|string} toOrgId
   * @param {string} role
   * @param {string} userUid
   * @param {boolean} forceOwnerTransfer - Force transfer even for owners
   */
  static async moveUserBetweenOrganizations(userEmail, fromOrgId, toOrgId, role = 'member', userUid, forceOwnerTransfer = false) {
    let addedToNewOrg = false;
    
    try {
      // First try to add to new org
      await this.addUserToOrganization(toOrgId, userEmail, role);
      addedToNewOrg = true;
      
      // For owner transfers, use special endpoint or force flag if available
      if (forceOwnerTransfer) {
        try {
          // Try special owner transfer endpoint first
          await axios.post(`${API_URL}/organizations/${fromOrgId}/transfer-owner`, {
            userUid,
            targetOrgId: toOrgId,
            newRole: role
          });
        } catch (ownerTransferError) {
          // If special endpoint doesn't exist, try regular removal with force flag
          await axios.delete(`${API_URL}/organizations/${fromOrgId}/users/${userUid}?force=true`);
        }
      } else {
        // Regular user removal
        await this.removeUserFromOrganization(fromOrgId, userUid);
      }
      
      return { success: true, message: 'User moved successfully', fromOrgId, toOrgId };
    } catch (error) {
      // If we added to new org but failed to remove from old org
      if (addedToNewOrg) {
        throw new Error(`User added to new organization but failed to remove from old organization: ${error.message}. User is now in both organizations.`);
      }
      
      // If we failed to add to new org, just throw the original error
      throw error;
    }
  }

  /**
   * Force remove user (including owners) from organization
   * @param {number|string} organizationId
   * @param {string} userUid
   * @param {boolean} force - Force removal even for owners
   */
  static async forceRemoveUserFromOrganization(organizationId, userUid, force = true) {
    const response = await axios.delete(`${API_URL}/organizations/${organizationId}/users/${userUid}?force=${force}`);
    return response.data;
  }

  /**
   * Transfer file ownership from one user to another
   * @param {string|null} sourceEmail - Current file owner's email (optional)
   * @param {string} fileId - File ID (UUID) to transfer
   * @param {string} destinationEmail - New file owner's email
   */
  static async transferFileOwnership(sourceEmail, fileId, destinationEmail) {
    const response = await axios.post(`${API_URL}/files/transfer-ownership`, {
      sourceEmail,
      fileId,
      destinationEmail
    });
    return response.data;
  }

  /**
   * Block a user by email
   * @param {string} userEmail - Email of user to block
   * @param {string} reason - Reason for blocking
   */
  static async blockUser(userEmail, reason = 'Blocked by admin') {
    const response = await axios.post(`${API_URL}/users/block`, {
      email: userEmail,
      reason
    });
    return response.data;
  }

  /**
   * Unblock a user by email
   * @param {string} userEmail - Email of user to unblock
   */
  static async unblockUser(userEmail) {
    const response = await axios.post(`${API_URL}/users/unblock`, {
      email: userEmail
    });
    return response.data;
  }

  /**
   * Get user block status by email
   * @param {string} userEmail - Email of user to check
   */
  static async getUserBlockStatus(userEmail) {
    const response = await axios.get(`${API_URL}/users/block-status/${encodeURIComponent(userEmail)}`);
    return response.data;
  }

}

export default CreditService; 